/* eslint-disable prettier/prettier */
import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import { EntryType, Nutrition, Programme, RecommendedMenu } from '../../API';
import { deduplicate, flattenObjects } from '../../services/arrays';
import {
  macroRatioFor,
  sumNutrition,
  targetDayNutritionFromMetricConstraints,
} from '../../services/nutrition';
import {
  DAY_TITLES,
  PROGRAMMES_DEFAULT_TARGET_CALORIES,
} from '../../services/programmes';
import Board from '../common/board';
import { useIsMobile } from '../common/layout_hooks';
import ProgrammePlanDayHeader from './programme_plan_day_header';
import smorg_loader from '../../assets/images/smorg_loader.gif';
import MealDetailModal from '../meal/meal_detail_modal';
import { mealWithScaledIngredients } from '../../services/meals';
import { cloneObject } from '../../operations/utils';
import ProgrammePreviewEntryCard from './programme_preview_entry_card';
import {
  RECOMMENDER_ENTRY_TYPE_MEAL,
  RECOMMENDER_MEAL_SOURCE_GRC,
  RECOMMENDER_MEAL_SOURCE_SMORG,
} from '../../services/recommender';
import {
  useGetGRCRecipesByIDsQuery,
  useGetFullMealsByParentsQuery,
} from '../../app/smorgAppSyncGraphQLApi';

interface ProgrammeBoardProps {
  programme: Programme | null;
  recommendedMenu: RecommendedMenu | null;
  recommenderErrorMessage: string | null;
  // calorieTarget: number;
  isLoading: boolean;
}

const components = {
  Card: ProgrammePreviewEntryCard,
  LaneHeader: ProgrammePlanDayHeader,
};

const labels: Record<string, unknown> = {
  'Add another lane': '+ Add another menu',
  'Delete lane': 'Delete menu',
  'Lane actions': 'Menu actions',
  button: {
    'Add lane': 'Add menu',
    'Add card': 'Add meal',
    Cancel: 'Cancel',
  },
  placeholder: {
    title: 'title',
    description: 'description',
    label: 'label',
  },
};

const customTranslation = (key: string) => labels[key];

const boardCss = { textAlign: 'left' };
const mobileHeightRule = { height: 'calc(var(--app-height) - 119px)' };
const desktopHeightRule = { height: 'calc(var(--app-height) - 100px)' };
const mobileBoardCss = { ...boardCss, ...mobileHeightRule };
const desktopBoardCss = { ...boardCss, ...desktopHeightRule };

const ProgrammePlanBoardPreview = ({
  programme,
  recommendedMenu,
  recommenderErrorMessage,
  isLoading,
}: ProgrammeBoardProps) => {
  const [showDetailForEntryID, setShowDetailForEntryID] = useState<
    string | null
  >(null);

  const {
    data: fullMealsByParents,
  } = useGetFullMealsByParentsQuery(programme?.recipesBoardIDs || [], {
    skip: (programme?.recipesBoardIDs || []).length === 0,
  });

  const fullMeals = flattenObjects(Object.values(fullMealsByParents || {}));

  const grcEntries = (recommendedMenu?.days || []).flatMap((day) =>
    day.entries
      .filter((e) => e.entryType === RECOMMENDER_ENTRY_TYPE_MEAL)
      .flatMap((e) =>
        e.meals.filter((m) => m.mealSource === RECOMMENDER_MEAL_SOURCE_GRC),
      ),
  );

  const grcRecipeIDs = grcEntries.map((e) => e.id);

  const { data: grcRecipes } = useGetGRCRecipesByIDsQuery(grcRecipeIDs || [], {
    skip: !grcRecipeIDs || grcRecipeIDs.length === 0,
  });

  const detailVisibleFor = useMemo(() => {
    if (!showDetailForEntryID || !recommendedMenu?.days) {
      return null;
    }
    const pair = recommendedMenu.days
      .flatMap((d) =>
        d.entries
          .filter((e) => e.entryType === RECOMMENDER_ENTRY_TYPE_MEAL)
          .flatMap((e) =>
            e.meals.map((m) => ({
              dayTitle: DAY_TITLES[d.dayIndex],
              entryType:
                m.mealSource === RECOMMENDER_MEAL_SOURCE_SMORG
                  ? EntryType.MEAL
                  : EntryType.GRC_RECIPE,
              meal: m,
            })),
          ),
      )
      .find(({ meal }) => meal.id === showDetailForEntryID);
    if (!pair) {
      return null;
    }
    return {
      entryID: showDetailForEntryID,
      entryType: pair.entryType,
      recommendedMeal: pair.meal,
      dayTitle: pair.dayTitle,
    };
  }, [recommendedMenu?.days, showDetailForEntryID]);

  console.log({ detailVisibleFor });

  const preferredMetrics = deduplicate(
    (programme?.nutritionConstraints?.nutritionMetricConstraints || []).map(
      (c) => c.nutritionMetric,
    ),
  ).slice(0, 3);

  const hasNutritionHeader = preferredMetrics.length > 0;

  // console.log(
  //   `ProgrammePlanBoardPreview hasNutritionHeader = ${hasNutritionHeader}`,
  // );

  if (hasNutritionHeader && !preferredMetrics.includes('calories')) {
    preferredMetrics.unshift('calories');
  }

  const preferredMetricsWithRules = preferredMetrics.map((metricName) => ({
    metricName,
    rules: [],
  }));

  const reactTrelloData = {
    lanes: (recommendedMenu?.days || []).map((day, dayIndex) => {
      const allEntries = day.entries
        .filter((e) => e.entryType === RECOMMENDER_ENTRY_TYPE_MEAL)
        .flatMap((e) => e.meals);
      const actualTotalNutrition = allEntries.reduce(
        (acc, m) => sumNutrition(acc, m.scaledNutrition || {}) as Nutrition,
        {},
      );

      const targetNutrition = targetDayNutritionFromMetricConstraints(
        programme?.nutritionConstraints?.nutritionMetricConstraints || [],
        preferredMetrics,
        PROGRAMMES_DEFAULT_TARGET_CALORIES,
      );

      const cards = allEntries
        .map((m) => {
          if (m.mealSource === RECOMMENDER_MEAL_SOURCE_SMORG) {
            const originalMeal = fullMeals[m.id];
            if (!originalMeal) {
              return null;
            }
            // const recommendedProvidedScaledIngredients = m.servings && m.scaledIngredientsFullText && m.scaledNutrition &&
            const meal = /* originalMeal */ mealWithScaledIngredients(
              cloneObject(originalMeal),
              m.servings,
              m.scaledIngredientsFullText || [],
              m.scaledNutrition,
              m.scaledStructuredIngredients || [],
              m.scaledDerivedIngredientNutrition || [],
            );
            return {
              id: m.id,
              title: meal.recipes[0].title,
              description: meal.recipes[0].shortDescription || '',
              style: {
                _meal: meal,
                _preferredMetricsWithRules: preferredMetricsWithRules,
                _targetNutrition: targetNutrition,
                _targetCalories: PROGRAMMES_DEFAULT_TARGET_CALORIES,
              },
            };
          }

          if (m.mealSource === RECOMMENDER_MEAL_SOURCE_GRC) {
            const grcRecipe = (grcRecipes || {})[m.id];
            if (!grcRecipe) {
              return null;
            }
            const meal =
              /* { recipes: [grcRecipe.recipe] } */ mealWithScaledIngredients(
                { recipes: [grcRecipe.recipe] },
                m.servings,
                m.scaledIngredientsFullText || [],
                m.scaledNutrition,
                m.scaledStructuredIngredients || [],
                m.scaledDerivedIngredientNutrition || [],
              );
            return {
              id: m.id,
              title: grcRecipe.recipe.title,
              description: grcRecipe.recipe.shortDescription || '',
              style: {
                _programmeEntryType: EntryType.GRC_RECIPE,
                _meal: meal,
                _preferredMetricsWithRules: preferredMetricsWithRules,
                _targetNutrition: targetNutrition,
                _targetCalories: PROGRAMMES_DEFAULT_TARGET_CALORIES,
              },
            };
          }

          return null;
        })
        .filter((cardData) => !!cardData);

      const actualMacroRatio = actualTotalNutrition
        ? macroRatioFor(actualTotalNutrition)
        : {};

      const titleStyle: Record<string, any> = { _isPreview: true };
      if (hasNutritionHeader) {
        titleStyle._nutritionHeader = {
          targetNutrition,
          actualTotalNutrition,
          preferredMetricsWithRules,
          targetCalories: PROGRAMMES_DEFAULT_TARGET_CALORIES,
          actualMacroRatio,
          showNutritionToUsers: programme?.showNutritionToUsers,
        };
      }

      return {
        id: uuidv4(),
        title: DAY_TITLES[dayIndex],
        titleStyle,
        cards,
      };
    }),
  };

  const onCardClick = useCallback((cardId: string) => {
    setShowDetailForEntryID(cardId);
  }, []);

  const isMobile = useIsMobile();

  if (isLoading) {
    return <img src={smorg_loader} alt="Please wait..." />;
  }

  const onDismissDetail = () => {
    setShowDetailForEntryID(null);
  };

  const showNutritionMetrics = [
    'calories',
    'fat',
    'carbohydrate',
    'protein',
    'fibre',
  ];

  let visibleDetailMeal = null;
  if (detailVisibleFor?.entryType === EntryType.MEAL) {
    visibleDetailMeal = mealWithScaledIngredients(
      cloneObject(fullMeals[detailVisibleFor.entryID]),
      detailVisibleFor.recommendedMeal.servings,
      detailVisibleFor.recommendedMeal.scaledIngredientsFullText || [],
      detailVisibleFor.recommendedMeal.scaledNutrition,
      detailVisibleFor.recommendedMeal.scaledStructuredIngredients || [],
      detailVisibleFor.recommendedMeal.scaledDerivedIngredientNutrition || [],
    );
  } else if (detailVisibleFor?.entryType === EntryType.GRC_RECIPE) {
    visibleDetailMeal = mealWithScaledIngredients(
      { recipes: [(grcRecipes || {})[detailVisibleFor.entryID].recipe] },
      detailVisibleFor.recommendedMeal.servings,
      detailVisibleFor.recommendedMeal.scaledIngredientsFullText || [],
      detailVisibleFor.recommendedMeal.scaledNutrition,
      detailVisibleFor.recommendedMeal.scaledStructuredIngredients || [],
      detailVisibleFor.recommendedMeal.scaledDerivedIngredientNutrition || [],
    );
  }

  return (
    <>
      <div
        className={classNames('smorg-board-container', {
          'with-fixed-header': hasNutritionHeader,
        })}
        id="programme-board">
        <Board
          laneDraggable={false}
          cardDraggable={false}
          data={reactTrelloData}
          components={components}
          onCardClick={onCardClick}
          style={isMobile ? mobileBoardCss : desktopBoardCss}
          backdropContent={recommenderErrorMessage}
          t={customTranslation}
        />
      </div>
      {detailVisibleFor?.entryType === EntryType.MEAL && (
        <MealDetailModal
          fullMeal={visibleDetailMeal}
          isLoading={false}
          menu={{ title: detailVisibleFor.dayTitle }}
          isReadOnly
          alwaysShowNutrition
          savingInProgress={false}
          showNutritionMetrics={showNutritionMetrics}
          derivedNutrition={visibleDetailMeal.derivedNutrition}
          canScaleIngredients={false}
          scaleToServings={1}
          currentlyEditingInline={{ section: '', itemId: null }}
          ingredientSuggestions={[]}
          ingredientSuggestionsNetworkState={{ loading: false }}
          onInlineEditFocus={() => {}}
          onInlineEditChange={() => {}}
          onInlineEditBlur={() => {}}
          onAddArraySectionItem={() => {}}
          onRemoveArraySectionItem={() => {}}
          onIngredientCheckChange={() => {}}
          onEditScaleToServings={() => {}}
          onChangeScaleToServings={() => {}}
          resetScaleToServings={() => {}}
          onNewImageChosen={() => {}}
          onRuleChanged={() => {}}
          onAddonChange={() => {}}
          onTagsChange={() => {}}
          onNewImageUrlSet={() => {}}
          onDialogClose={onDismissDetail}
          onUpdateIngredientStructuredData={() => {}}
        />
      )}
      {detailVisibleFor?.entryType === EntryType.GRC_RECIPE && (
        <MealDetailModal
          fullMeal={visibleDetailMeal}
          isLoading={false}
          menu={{ title: detailVisibleFor.dayTitle }}
          isReadOnly
          alwaysShowNutrition
          savingInProgress={false}
          showNutritionMetrics={showNutritionMetrics}
          derivedNutrition={visibleDetailMeal.derivedNutrition}
          canScaleIngredients={false}
          scaleToServings={1}
          currentlyEditingInline={{ section: '', itemId: null }}
          ingredientSuggestions={[]}
          ingredientSuggestionsNetworkState={{ loading: false }}
          onInlineEditFocus={() => {}}
          onInlineEditChange={() => {}}
          onInlineEditBlur={() => {}}
          onAddArraySectionItem={() => {}}
          onRemoveArraySectionItem={() => {}}
          onIngredientCheckChange={() => {}}
          onEditScaleToServings={() => {}}
          onChangeScaleToServings={() => {}}
          resetScaleToServings={() => {}}
          onNewImageChosen={() => {}}
          onRuleChanged={() => {}}
          onAddonChange={() => {}}
          onTagsChange={() => {}}
          onNewImageUrlSet={() => {}}
          onDialogClose={onDismissDetail}
          onUpdateIngredientStructuredData={() => {}}
        />
      )}
    </>
  );
};

export default ProgrammePlanBoardPreview;
